"use client";

import * as React from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { Loader2, CheckCircle2, AlertCircle, RotateCw, X } from "lucide-react";
import { cn } from "../../lib/cn";
import { Progress } from "../../ui/progress";
import { springs, transitions } from "../../lib/motion";
import { getFileIconForName, type UploadFile } from "../../lib/fileUtils";

/**
 * FileItem — one row of the upload queue.
 *
 * Ported from the per-row body of Creator Studio's `FileUploadList.tsx`.
 * The headline behavior is the **status-pill morph**: the trailing pill
 * cross-fades between `uploading` (spinner + "Uploading"), `success` (check +
 * "Uploaded", `success` token) and `error` (alert + "Failed", `destructive`
 * token, plus a retry button). The donor inlined two raw `<svg>` spinners and
 * tinted rows with `bg-red-50` / `bg-green-50` / `bg-gray-50`; here the tint
 * uses `success` / `destructive` / `muted` tokens and the spinner is the
 * lucide `Loader2` with a token color.
 *
 * Motion: the pill swap is an `AnimatePresence` cross-fade and the `Progress`
 * width animates via a spring. On `error` the row does a short shake to draw
 * the eye to the retry affordance. Everything is gated on
 * `useReducedMotion()` — reduced-motion users get instant pill swaps, no
 * shake, and the progress bar still moves (it is the determinate signal).
 *
 * Names longer than 60 chars are truncated mid-string (matching the donor's
 * `slice(0, 57) + "..."`). a11y: the status pill exposes the state via text +
 * `role="status"`; retry / remove are labelled icon buttons.
 *
 * Typically rendered inside `<UploadQueue>`; exported standalone for custom
 * layouts.
 */
export interface FileItemProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "id" | "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
  > {
  /** The file to render. */
  file: UploadFile;
  /** Retry handler — shown as a button when `status === "error"`. */
  onRetry?: (id: string) => void;
  /** Remove handler — shows an `X` button when provided. */
  onRemove?: (id: string) => void;
}

const truncateName = (name: string) => (name.length > 60 ? `${name.slice(0, 57)}...` : name);

interface StatusPillProps {
  status: UploadFile["status"];
  reduced: boolean;
}

const StatusPill: React.FC<StatusPillProps> = ({ status, reduced }) => {
  const content =
    status === "uploading" ? (
      <span
        role="status"
        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
      >
        <Loader2 className={cn("size-3", !reduced && "animate-spin")} aria-hidden />
        Uploading
      </span>
    ) : status === "success" ? (
      <span
        role="status"
        className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-1 text-xs font-medium text-success"
      >
        <CheckCircle2 className="size-3" aria-hidden />
        Uploaded
      </span>
    ) : status === "error" ? (
      <span
        role="status"
        className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-1 text-xs font-medium text-destructive"
      >
        <AlertCircle className="size-3" aria-hidden />
        Failed
      </span>
    ) : null;

  if (!content) return null;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={status}
        initial={reduced ? false : { opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
        transition={transitions.snappy}
      >
        {content}
      </motion.span>
    </AnimatePresence>
  );
};

export const FileItem = React.forwardRef<HTMLDivElement, FileItemProps>(
  ({ file, onRetry, onRemove, className, ...rest }, ref) => {
    const reduced = useReducedMotion();
    const shake = file.status === "error" && !reduced ? { x: [0, -4, 4, -3, 3, 0] } : undefined;

    return (
      <motion.div
        ref={ref}
        animate={shake}
        transition={{ duration: 0.4 }}
        className={cn(
          "flex items-start gap-3 rounded-lg p-3 transition-colors",
          file.status === "error"
            ? "bg-destructive/5"
            : file.status === "success"
              ? "bg-success/5"
              : "bg-muted/40",
          className,
        )}
        {...rest}
      >
        <span className="mt-0.5 shrink-0">{file.icon ?? getFileIconForName(file.name)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate pr-2 text-sm font-medium text-foreground" title={file.name}>
              {truncateName(file.name)}
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              <StatusPill status={file.status} reduced={reduced ?? false} />
              {file.status === "error" && onRetry ? (
                <button
                  type="button"
                  onClick={() => onRetry(file.id)}
                  aria-label={`Retry uploading ${file.name}`}
                  className="rounded-md p-1 text-primary transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <RotateCw className="size-3.5" aria-hidden />
                </button>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(file.id)}
                  aria-label={`Remove ${file.name}`}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
          <div className="mt-1 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">{file.size}</p>
            {file.status === "uploading" && file.progress !== undefined ? (
              <Progress value={file.progress} className="h-1 flex-1" />
            ) : null}
          </div>
          {file.status === "error" && file.error ? (
            <p className="mt-1 text-xs text-destructive">{file.error}</p>
          ) : null}
        </div>
      </motion.div>
    );
  },
);
FileItem.displayName = "FileItem";

/** Spring used by `UploadQueue` to animate row entrance. Exported for reuse. */
export const fileItemEnterSpring = springs.gentle;
